import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ExternalLink, FileText, Layers, ListChecks, Loader2 } from "lucide-react";
import GlowCard from "../components/GlowCard";
import StatusBadge from "../components/StatusBadge";
import StepTracker, { TrackerStep } from "../components/StepTracker";
import SectionTreeView from "./SectionTreeView";
import RequirementsTab from "./RequirementsTab";
import { documentUrl, loadReview, listSections } from "./api";
import { ReviewState, VerityStandard, VeritySection } from "./types";

interface StandardWorkspaceProps {
  standard: VerityStandard;
  onBack: () => void;
  /**
   * Reading a standard, not building it.
   *
   * Everything that BUILDS the library — processing sections, correcting what the extractor made of
   * them, publishing a rule set — is the administrator's. A reader gets the same four screens with
   * the same content, because seeing which clause a finding will cite is the point of looking.
   *
   * Applied two ways: the processing and publish controls are not rendered at all, and `vr-readonly`
   * on the root takes out the per-requirement rulings deeper in. Both are UX, not security — the
   * routes behind them carry their own permission checks.
   */
  readOnly?: boolean;
  /**
   * Walk the four screens once, on arrival from the upload.
   *
   * Somebody who has just added their first standard has never seen this workbench. Landing them
   * straight on Requirements gives them the deepest screen with no idea the other three exist —
   * so the tracker walks Source → Overview → Structure → Requirements, two seconds each, showing the
   * real page every time. It runs once and only for a standard that was just created.
   */
  introduce?: boolean;
}

/** How long each screen is held during the introduction. */
const INTRO_STEP_MS = 2000;
const INTRO_ORDER: Tab[] = ["source", "overview", "structure", "requirements"];

type Tab = "overview" | "structure" | "requirements" | "source";

/**
 * The order the work actually goes in, which is not the order the tabs were in.
 *
 * Source is the document as received; Overview is what was made of it; Structure is the tree that
 * was built; Requirements is what was read out of that tree. Numbering them says so, and it is the
 * same tracker a project's stages use — the two are the same idea and now look like it.
 */
const TABS: TrackerStep<Tab>[] = [
  { key: "source", label: "Source" },
  { key: "overview", label: "Overview" },
  { key: "structure", label: "Structure" },
  { key: "requirements", label: "Requirements" },
];

/**
 * One standard, opened.
 *
 * The library is a catalogue; this is the workbench. Keeping them apart is what lets a standard have a
 * substantial working environment — three hundred sections to walk, a rule layer to validate, a
 * document to check it against — without the catalogue itself becoming that.
 *
 * Structure is what IQ Verity understood the document to BE. Requirements is what it understood the
 * document to REQUIRE. The first is derived and stable; the second is a proposal a person is here to
 * accept, correct or reject, and the tabs keep that difference visible.
 */
const StandardWorkspace = ({
  standard,
  onBack,
  introduce = false,
  readOnly = false,
}: StandardWorkspaceProps) => {
  const [tab, setTab] = useState<Tab>(introduce ? "source" : "requirements");
  const [sections, setSections] = useState<VeritySection[] | null>(null);
  const [review, setReview] = useState<ReviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** True while the introduction is walking itself. Nothing is clickable until it settles. */
  const [touring, setTouring] = useState(introduce);
  const tourTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [s, r] = await Promise.all([listSections(standard.id), loadReview(standard.id)]);
      setSections(s);
      setReview(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this standard.");
    }
  }, [standard.id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The introduction, started only once the sections are in.
   *
   * Waiting for the data is the point: each screen has to have its real content on it — the document,
   * the counts, the tree — or the walk shows four empty layouts and teaches nothing.
   */
  useEffect(() => {
    if (!touring || !sections) return;
    tourTimers.current = INTRO_ORDER.slice(1).map((next, i) =>
      setTimeout(() => {
        setTab(next);
        if (i === INTRO_ORDER.length - 2) setTouring(false);
      }, (i + 1) * INTRO_STEP_MS),
    );
    return () => tourTimers.current.forEach(clearTimeout);
  }, [touring, sections]);

  const published = review?.ruleSets?.[0];
  const parts = standard.documents ?? [];

  // Requirements is a workspace rather than a document: its frame — back link, title, tabs, counts —
  // is pinned and only the chapter list scrolls. The other tabs are ordinary pages and scroll whole.
  const fixedHead = tab === "requirements";

  return (
    <div className={`vr-page ${fixedHead ? "vr-page-fixed" : ""}${readOnly ? " vr-readonly" : ""}`}>
      <div className="vr-page-head">
        <div>
          <button className="vr-rowbtn" onClick={onBack} style={{ marginBottom: ".75rem" }}>
            <ArrowLeft size={12} /> Standards Library
          </button>
          <h1 className="vr-h-page">{standard.title}</h1>
          <p style={{ marginTop: ".25rem" }}>
            {standard.authority} · {standard.edition} · {standard.jurisdictionPath.join(" › ")}
            {standard.shortCode && <> · {standard.shortCode}</>}
          </p>
        </div>

        {/* What this standard currently offers a project, stated plainly. A partially reviewed
            standard must never read as a finished rule set. */}
        <div className="vr-ws-status">
          {published ? (
            <>
              <StatusBadge
                status={published.coverage.complete ? "pass" : "warning"}
                label={`Published v${published.version}`}
              />
              <span className="vr-cell-code">
                {published.ruleCount} requirements ·{" "}
                {published.coverage.complete
                  ? "complete standard"
                  : `${published.coverage.reviewedSections} of ${published.coverage.totalSections} sections`}
              </span>
            </>
          ) : (
            <StatusBadge status="info" label="Not published" />
          )}
        </div>
      </div>

      {/* A step ticks once it has produced something: the document is there, it was extracted, the
          tree was built, requirements were published. Never a gate — every circle stays clickable. */}
      <StepTracker
        steps={TABS}
        active={tab}
        onSelect={setTab}
        locked={touring}
        done={[
          ...(parts.length > 0 ? (["source"] as Tab[]) : []),
          ...(standard.extraction ? (["overview"] as Tab[]) : []),
          ...((standard.extraction?.sectionCount ?? 0) > 0 ? (["structure"] as Tab[]) : []),
          ...(published ? (["requirements"] as Tab[]) : []),
        ]}
      />

      {error && <div className="vr-form-error">{error}</div>}

      {!sections && !error && (
        <div className="vr-empty">
          <Loader2 size={16} className="vr-spin-inline" /> Loading…
        </div>
      )}

      {/* The page underneath is the real one, with its real content — the spinner sits over it so it
          reads as "still working" rather than as a screen you were meant to act on. */}
      {touring && (
        <div className="vr-tour-veil">
          <Loader2 size={26} className="vr-spin-inline" />
        </div>
      )}

      {tab === "overview" && sections && (
        <div className="vr-grid vr-grid-3">
          <GlowCard className="vr-feature" hover={false}>
            <div className="vr-feature-icon">
              <Layers size={18} />
            </div>
            <h3>Structure</h3>
            <p>
              {standard.extraction?.sectionCount ?? sections.length} sections across{" "}
              {standard.extraction?.pageCount ?? 0} pages
              {(standard.extraction?.rasterPageCount ?? 0) > 0 && (
                <>
                  , of which {standard.extraction?.rasterPageCount} pages carry no text layer and
                  cannot be read
                </>
              )}
              .
            </p>
          </GlowCard>

          <GlowCard className="vr-feature" hover={false}>
            <div className="vr-feature-icon">
              <ListChecks size={18} />
            </div>
            <h3>Requirements</h3>
            <p>
              {review?.rules.filter((r) => r.state !== "excluded").length ?? 0} candidate requirements,{" "}
              {review?.rules.filter((r) => r.state === "approved" || r.state === "edited").length ?? 0}{" "}
              validated across{" "}
              {review?.sectionReviews.filter(
                (s) => s.state === "reviewed" || s.state === "no-requirements",
              ).length ?? 0}{" "}
              settled sections.
            </p>
          </GlowCard>

          <GlowCard className="vr-feature" hover={false}>
            <div className="vr-feature-icon">
              <FileText size={18} />
            </div>
            <h3>Source</h3>
            <p>
              {parts.length || 1} document{parts.length > 1 ? "s" : ""}. Stored exactly as supplied and
              never modified — every requirement points back into it.
            </p>
          </GlowCard>
        </div>
      )}

      {tab === "structure" && sections && <SectionTreeView standard={standard} />}

      {tab === "requirements" && sections && review && (
        <RequirementsTab
          standard={standard}
          sections={sections}
          review={review}
          onReload={load}
          readOnly={readOnly}
        />
      )}

      {tab === "source" && (
        <GlowCard className="vr-table-card" hover={false}>
          <div className="vr-table-head">
            <h3>Source document{parts.length > 1 ? "s" : ""}</h3>
          </div>
          <div className="vr-file-list" style={{ padding: "1.25rem" }}>
            {(parts.length ? parts : [null]).map((doc, i) => (
              <a
                key={doc?.docId ?? i}
                className="vr-file-row vr-file-pick"
                href={documentUrl(standard.id, doc?.docIndex ?? 0)}
                target="_blank"
                rel="noreferrer noopener"
              >
                <FileText size={15} style={{ color: "hsl(var(--vr-primary))" }} />
                <span className="vr-file-name">
                  {parts.length > 1 && <span className="vr-part-no">Part {(doc?.docIndex ?? 0) + 1}</span>}
                  {doc?.fileName ?? standard.fileName ?? "Source document"}
                </span>
                <ExternalLink size={13} className="vr-cell-dim" />
              </a>
            ))}
          </div>
        </GlowCard>
      )}
    </div>
  );
};

export default StandardWorkspace;
